import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Github, Linkedin, Check, Copy } from 'lucide-react';
import { personalInfo, contactData } from '../data/personalData';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const ContactPage: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(personalInfo.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — mailto link below still works
    }
  };

  return (
    <div className="min-h-screen bg-background bg-dotgrid flex flex-col">
      <Navbar />

      <section className="flex-1 pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="font-mono text-sm text-accent mb-2">$ cat contact.ini</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-text mb-3">Let's talk.</h1>
            <p className="text-muted">
              I'm open to Data Analyst roles and collaborations. Email is the fastest way to reach me.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-lg border border-border bg-surface overflow-hidden"
          >
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-raised border-b border-border">
              <span className="w-2.5 h-2.5 rounded-full bg-danger/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-accent/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-accent2/60" />
              <span className="ml-3 text-xs font-mono text-muted">contact.ini</span>
            </div>

            <div className="p-6 font-mono text-sm space-y-4">
              <div className="flex items-center justify-between gap-3 group">
                <div className="flex items-center gap-3 min-w-0">
                  <Mail size={16} className="text-accent shrink-0" />
                  <div className="min-w-0">
                    <div className="text-muted text-xs">email</div>
                    <a
                      href={`mailto:${personalInfo.email}`}
                      className="text-text hover:text-accent transition-colors truncate block"
                    >
                      {personalInfo.email}
                    </a>
                  </div>
                </div>
                <button
                  onClick={copyEmail}
                  className="text-muted hover:text-accent transition-colors shrink-0"
                  aria-label="Copy email"
                >
                  {copied ? <Check size={16} className="text-accent2" /> : <Copy size={16} />}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-accent shrink-0" />
                <div>
                  <div className="text-muted text-xs">location</div>
                  <span className="text-text">{contactData.address}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Github size={16} className="text-accent shrink-0" />
                <div>
                  <div className="text-muted text-xs">github</div>
                  <a
                    href={contactData.profiles.github}
                    target="_blank"
                    rel="noreferrer"
                    className="text-text hover:text-accent transition-colors"
                  >
                    github.com/adityabharti83
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Linkedin size={16} className="text-accent shrink-0" />
                <div>
                  <div className="text-muted text-xs">linkedin</div>
                  <a
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="text-text hover:text-accent transition-colors"
                  >
                    linkedin.com/in/aditya-bharti
                  </a>
                </div>
              </div>

              <div className="pt-3 border-t border-border">
                <span className="text-muted">status</span>
                <span className="text-muted"> = </span>
                <span className="text-accent2">"open to opportunities"</span>
              </div>
            </div>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            href={`mailto:${personalInfo.email}?subject=Let's connect`}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-accent text-background font-mono text-sm font-semibold rounded hover:bg-accent/90 transition-colors"
          >
            <Mail size={16} />
            send_email()
          </motion.a>
        </div>
      </section>

      <Footer />
    </div>
  );
};
